import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Rating from '@mui/material/Rating';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';

// Evaluates whether the current answer triggers a critical finding.
function isCritical(question, value) {
  const rule = question.critical_if;
  if (!rule || value == null || value === '') return false;
  if (rule.yes === true && value === 'yes') return true;
  if (rule.no === true && value === 'no') return true;
  if (typeof rule.rating_lte === 'number' && Number(value) <= rule.rating_lte) return true;
  if (Array.isArray(rule.includes) && Array.isArray(value)) {
    return value.some((v) => rule.includes.includes(v));
  }
  return false;
}

export default function QuestionRenderer({
  question,
  response = {},
  onChange,
  uploadPhoto,
}) {
  const value = response.value;
  const notes = response.notes || '';
  const critical = isCritical(question, value);

  const setValue = (v) => onChange({ value: v, notes });
  const setNotes = (n) => onChange({ value, notes: n });

  const ratingNotesRequired =
    question.type === 'rating_1_5' && typeof value === 'number' && value <= 2;

  return (
    <Box
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: critical ? 'error.main' : 'divider',
        backgroundColor: critical ? '#fff5f5' : 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', mt: 0.25 }}>
          {question.number}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
          {question.prompt}
          {question.required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
        </Typography>
        {question.help && (
          <Tooltip
            title={<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.85rem' }}>{question.help}</Box>}
            arrow
            placement="top-end"
            enterTouchDelay={0}
          >
            <IconButton size="small" sx={{ color: 'info.main', mt: -0.5 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {critical && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <WarningAmberIcon fontSize="small" color="error" />
          <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
            Critical finding — will be highlighted in the client report.
          </Typography>
        </Box>
      )}

      <Box sx={{ pl: 4 }}>
        {question.type === 'yes_no' && (
          <RadioGroup row value={value || ''} onChange={(e) => setValue(e.target.value)}>
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            <FormControlLabel value="na" control={<Radio size="small" />} label="N/A" />
          </RadioGroup>
        )}

        {question.type === 'rating_1_5' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Rating
              value={value || 0}
              onChange={(_, v) => setValue(v || null)}
              max={5}
            />
            <Typography variant="caption" color="text.secondary">
              {value ? `${value} / 5` : 'Not rated'}
            </Typography>
          </Box>
        )}

        {question.type === 'text' && (
          <TextField
            value={value || ''}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Your answer..."
            size="small"
          />
        )}

        {question.type === 'multi_select' && (
          <FormGroup row>
            {(question.options || []).map((opt) => {
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.includes(opt);
              return (
                <FormControlLabel
                  key={opt}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...arr, opt]
                          : arr.filter((x) => x !== opt);
                        setValue(next);
                      }}
                    />
                  }
                  label={opt}
                />
              );
            })}
          </FormGroup>
        )}

        {question.type === 'photo' && (
          <PhotoUpload
            questionId={question.id}
            value={value}
            onChange={setValue}
            uploadPhoto={uploadPhoto}
          />
        )}
      </Box>

      <TextField
        label={ratingNotesRequired ? 'Notes (required for low ratings)' : 'Notes (optional)'}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        size="small"
        multiline
        rows={1}
        sx={{ mt: 2 }}
        error={ratingNotesRequired && !notes.trim()}
      />
    </Box>
  );
}

function PhotoUpload({ questionId, value, onChange, uploadPhoto }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadPhoto(file, questionId);
      onChange(url);
    } catch (err) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      {value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={value}
            alt="evidence"
            sx={{
              width: 80,
              height: 80,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
          <Chip label="Uploaded" size="small" color="success" />
          <IconButton size="small" onClick={() => onChange(null)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Button
          variant="outlined"
          component="label"
          startIcon={<PhotoCameraIcon />}
          disabled={uploading}
          size="small"
        >
          {uploading ? 'Uploading…' : 'Take / Upload Photo'}
          {/*
            No `capture` attribute — that hint locks up the picker dialog on
            some desktop browsers (the OS tries to open a webcam-capture flow
            that doesn't return cleanly). Mobile file pickers still offer
            "Take Photo" / "Photo Library" options without it.
          */}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFile}
          />
        </Button>
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
